import { Immutable } from "immer";
import React from "react";
import { ProductCompareGridSSR } from "tsi-common-react/src/apps/product-compare/ProductCompareGridSSR/index";
import { ProductSelectionGridSSR } from "tsi-common-react/src/apps/product-compare/ProductSelectionGridSSR/index";
import { ProductGridSSR } from "tsi-common-react/src/apps/product-grid2/ProductGridSSR/index";
import { RetailCardsSSR } from "tsi-common-react/src/apps/retail/RetailCardsSSR/index";
import { AccordionBlock } from "tsi-common-react/src/components/AccordionBlock/index";
import { BannerBlock } from "tsi-common-react/src/components/BannerBlock/index";
import { ColumnsCarouselBlock } from "tsi-common-react/src/components/ColumnsCarouselBlock/index";
import { CompatibilityAccordion } from "tsi-common-react/src/components/CompatibilityAccordion/index";
import { EmailCaptureContent } from "tsi-common-react/src/components/EmailCaptureContent/index";
import { FAQsBlock } from "tsi-common-react/src/components/FAQsBlock/index";
import { FeaturedProductLinesBlock } from "tsi-common-react/src/components/FeaturedProductLinesBlock/index";
import { HomepageHeroPromoBox } from "tsi-common-react/src/components/HomepageHeroPromoBox/index";
import { PreApprovalBlock } from "tsi-common-react/src/components/PreApprovalBlock/index";
import { ProductCalloutBlock } from "tsi-common-react/src/components/ProductCalloutBlock/index";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { TSIAlignedRichTextBlock } from "tsi-common-react/src/components/TSIAlignedRichTextBlock/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { WarrantyForm } from "tsi-common-react/src/components/WarrantyForm/index";

import { templates } from "@reactivated";
import type {
    CompatibilityAccordionModule as CompatibilityAccordionValue,
    EmailCaptureBlock as EmailCaptureBlockValue,
    StearnsFinancingBannerBlock as FinancingBannerBlockValue,
    LinkBlock as LinkBlockValue,
    StearnsPromoConditionalBlock as PromoConditionalBlockValue,
    PromotionBlock as PromotionBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
    ValuePropBarBlock as ValuePropBarBlockValue,
    SharedWarrantyForm as WarrantyFormValue,
} from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { RetailLocatorSSR } from "../apps/retail-locator/RetailLocatorSSR";
import { FinancingBannerBlock } from "../components/FinancingBannerBlock";
import { DetailBlock } from "./DetailBlock";
import { DetailSliderBlock } from "./DetailSliderBlock";
import { FeatureBlock } from "./FeatureBlock";
import { FeaturedProductsBlock } from "./FeaturedProductsBlock";
import { GalleryBlock } from "./GalleryBlock";
import { HeroBlock } from "./HeroBlock";
import { IframeBlock } from "./IframeBlock";
import { MegaHeroBlock } from "./MegaHeroBlock";
import { RetailFeatureBlock } from "./RetailFeatureBlock";
import { SocialGridBlock } from "./SocialGridBlock";
import { StearnsAnchorBlock } from "./StearnsAnchorBlock";
import { StearnsCompetitionCompareTableBlock } from "./StearnsCompetitionCompareTableBlock";
import { TabsBlock } from "./TabsBlock";

/** Stearns warranty-form constants (source: server/templates/cms/blocks/warranty-form-block.html). */
const STEARNS_WARRANTY_PROPERTY_NAME = "Stearns & Foster®";
const STEARNS_WARRANTY_CLAIM_URL =
    "https://help.stearnsandfoster.com/hc/en-us/requests/new?ticket_form_id=1500003170642";

/**
 * Shared Toolbox block dispatcher map for every Stearns page-body assembly.
 * Keys are the EXACT block-name strings from the Python ``ToolboxContent`` /
 * ``ToolboxContainers`` StreamBlocks; consumers spread this map and layer their
 * own page-specific overrides on top.
 *
 * Built inside ``buildToolboxComponentsMap`` so the recursive
 * ``promo_conditional_block`` entry can reference the full map for its inner
 * ``content`` StreamBlock (which is itself a ``ToolboxContent``).
 */
const buildToolboxComponentsMap = () => {
    const map = {
        // --- Simple {value} dispatch (component accepts `value` directly) ---
        accordion: AccordionBlock,
        columns_carousel_block: ColumnsCarouselBlock,
        // `common_banner` uses the shared CommonBannerBlock server-side; React
        // side is the single BannerBlock component.
        common_banner: BannerBlock,
        competition_compare_table_block: StearnsCompetitionCompareTableBlock,
        slider: DetailSliderBlock,
        detail: DetailBlock,
        feature: FeatureBlock,
        hero: HeroBlock,
        mega_hero: MegaHeroBlock,
        gallery: GalleryBlock,
        tabs: TabsBlock,
        social_grid: SocialGridBlock,
        retail_feature: RetailFeatureBlock,
        iframe: IframeBlock,
        anchor: StearnsAnchorBlock,
        faqs_block: FAQsBlock,
        homepage_hero_promo_box: HomepageHeroPromoBox,
        featured_product_lines_block: FeaturedProductLinesBlock,
        // Stearns-specific product grid; hydrates each card's star rating from
        // the chosen product pk client-side (price is a literal CharBlock).
        featured_products: FeaturedProductsBlock,
        pre_approval_block: PreApprovalBlock,
        aligned_rich_text_block: TSIAlignedRichTextBlock,
        product_callout: ProductCalloutBlock,

        // `compatibility` takes `value` (StructBlock). Wrapped in the themed
        // `<section>` that the standalone `compatibility-accordion-module.html`
        // emits (`<section class="{{ value.background_style.style }}">`). The
        // wrapper MUST live here, NOT in the shared `CompatibilityAccordion`
        // component or `CompatibilityAccordionTemplate`, because the legacy
        // per-block `{% include_block %}` path (cocoon/tempurpedic) already wraps
        // those in the `.html` `<section>` -- wrapping inside the component would
        // double-wrap them. The consolidated dispatcher reaches the component
        // raw (bypassing the `.html`), so it owns the wrapper. `background_style`
        // is serialized by `CompatibilityAccordionModule.get_data_for_client_
        // render` (flat `{style}`).
        compatibility: (block: {
            value: Immutable<CompatibilityAccordionValue>;
        }) => (
            <section className={block.value.background_style?.style ?? ""}>
                <CompatibilityAccordion value={block.value} />
            </section>
        ),

        // `rich_text` -> thelabui RichTextBlock (raw rich-text string value).
        rich_text: (block: { value: Immutable<RichTextBlockValue> }) => (
            <RichTextBlock value={block.value} />
        ),

        // `html` -> RawHTMLBlock (raw HTML string value).
        html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
            <RawHTMLBlock value={block.value} />
        ),

        // `value_props` -> ValuePropsBarBlock (prop is `valuePropsData`).
        value_props: (block: { value: Immutable<ValuePropBarBlockValue> }) => (
            <ValuePropsBarBlock valuePropsData={block.value} />
        ),

        // `promotion_block` -> needs 1-indexed `blockIndex` for the GA4
        // `view_promotion` creative_slot (same convention as pillows).
        promotion_block: (block: {
            index?: number;
            value: Immutable<PromotionBlockValue>;
        }) => (
            <PromotionBlock
                value={block.value}
                blockIndex={(block.index ?? 0) + 1}
            />
        ),

        // `email_capture` -> reproduces email-capture-block.html: maps the
        // block's `email_snippet` into the EmailCaptureContent shape and wraps
        // it in the themed `<section>` (background_style + responsive_visibility
        // classes the standalone template adds). The responsive_visibility class
        // is the functional half — it hides the block per breakpoint, so
        // dropping the wrapper could surface a block the CMS configured hidden.
        // Renders nothing when the snippet is absent.
        email_capture: (block: {
            value: Immutable<EmailCaptureBlockValue>;
        }) => {
            const snippet = block.value.email_snippet;
            if (!snippet) {
                return null;
            }
            return (
                <section
                    className={concatClassNames([
                        "email-capture-block",
                        block.value.background_style?.style ?? "",
                        block.value.responsive_visibility?.visibility ?? "",
                    ])}
                >
                    <EmailCaptureContent
                        content={{
                            email_snippet: {
                                title: snippet.title ?? "",
                                content: snippet.content,
                                confirm_content: snippet.confirm_content,
                                image: snippet.image,
                                footnotes: snippet.footnotes,
                            },
                        }}
                    />
                </section>
            );
        },

        // `financing_banner` -> gated on the serialized
        // `PreQualificationSettings.toolbox` flag (`value.toolbox_enabled`).
        // Renders nothing when toolbox pre-qual is toggled off in Global
        // Settings — matching the legacy `{% if prequal_settings.toolbox %}`
        // gate. applicationSource="Financing Page".
        financing_banner: (block: {
            value: Immutable<FinancingBannerBlockValue>;
        }) =>
            block.value?.toolbox_enabled ? (
                <FinancingBannerBlock applicationSource="Financing Page" />
            ) : null,

        // `warranty_form` -> declared on the base `ToolboxContent` (so it is
        // available on every toolbox page; `UtilityToolbox` redundantly
        // re-declares the same block). propertyName/claimURL constants come from
        // warranty-form-block.html (see constants above).
        warranty_form: (block: { value: Immutable<WarrantyFormValue> }) => (
            <WarrantyForm
                value={block.value}
                propertyName={STEARNS_WARRANTY_PROPERTY_NAME}
                claimURL={STEARNS_WARRANTY_CLAIM_URL}
            />
        ),

        // `link` -> NOT ported (blocked on tracking). Inline-rendered to match
        // the legacy link-block.html (display name + optional button styling).
        link: (block: { value: Immutable<LinkBlockValue> }) => (
            <a
                // `||` (not `??`) so an empty-string `external_url` falls through
                // to "#", matching link-block.html's Django-falsiness `{% if %}`.
                href={block.value.page?.url || block.value.external_url || "#"}
                className={block.value.is_button ? "button" : undefined}
            >
                {block.value.display_name}
            </a>
        ),

        // --- SSR-envelope blocks (`block.value` carries the full server-
        // computed SSR props; see the header docstring) ---

        // `product_grid` -> ProductGridSSR. `block.value` matches
        // `ProductGridBlockTemplate` (products_data, filters_data, sorters_data,
        // initial_filter_state, initial_sort_method, category_id, hide_sort,
        // disable_hover_overlay). Mirrors ProductGridBlockTemplate.tsx.
        product_grid: (block: {
            value: Immutable<templates.ProductGridBlockTemplate> | null;
        }) => (block.value ? <ProductGridSSR {...block.value} /> : null),

        // `product_compare_grid` -> ProductCompareGridSSR. Mirrors the stearns
        // ProductCompareGridBlockTemplate.tsx, which always passes
        // `showFirstAvailablePrice` + `isMinimal`.
        product_compare_grid: (block: {
            value: Immutable<templates.ProductCompareGridBlockTemplate> | null;
        }) =>
            block.value ? (
                <ProductCompareGridSSR
                    {...block.value}
                    showFirstAvailablePrice
                    isMinimal
                />
            ) : null,

        // `product_selection_grid` -> ProductSelectionGridSSR. Plain spread;
        // the shared ProductSelectionGridBlockTemplate.tsx passes no extra props
        // (`showStartingAt` is optional and derived from the CMS theme).
        product_selection_grid: (block: {
            value: Immutable<templates.ProductSelectionGridBlockTemplate> | null;
        }) =>
            block.value ? <ProductSelectionGridSSR {...block.value} /> : null,

        // `retail_cards` -> RetailCardsSSR. Empty serialized value; the SSR
        // container reads Redux/store-prefill. Mirrors RetailCardsBlockTemplate.
        retail_cards: () => <RetailCardsSSR />,

        // `retail_locator` -> stearns-local RetailLocatorSSR. Empty serialized
        // value; SSR driven. Mirrors StearnsRetailLocatorBlockTemplate.tsx.
        retail_locator: () => <RetailLocatorSSR />,

        // --- RECURSIVE: `promo_conditional_block` (ToolboxContainers) ---
        // Its `content` StreamBlock IS a ToolboxContent, so it dispatches via
        // this same map. `map` is captured by reference below.
        promo_conditional_block: (block: {
            value: Immutable<PromoConditionalBlockValue>;
        }) => (
            <PromoConditionalBlock value={block.value}>
                <StreamField
                    tagName={React.Fragment}
                    value={block.value.content ?? []}
                    components={map}
                />
            </PromoConditionalBlock>
        ),
    } as const;
    return map;
};

export const toolboxComponentsMap = buildToolboxComponentsMap();
